import { Chart } from "chart.js"
import { arrayMax, arrayMin, sum } from "../utils/array"

type ExtendedChart = Chart & { chart: { canvas: HTMLCanvasElement } }

interface Config {
    chartConfig: Chart.ChartConfiguration
    destroyOnRefresh?: boolean
}

export function setupChart(chart: Chart, rootElement: HTMLElement, config: Config, fixedLeftAxes: boolean, fixedRightAxes: boolean) {

    const canvas = rootElement.querySelector("canvas.chart") as HTMLCanvasElement
    const ctx = canvas.getContext("2d")

    setupYAxes(rootElement, config.chartConfig, fixedLeftAxes, fixedRightAxes)

    if (!chart) {

        chart = new Chart(ctx, config.chartConfig)

    }
    else {

        if (config.destroyOnRefresh) {
            chart.destroy()
            chart = new Chart(ctx, config.chartConfig)
        } {
            chart.config = config.chartConfig
            chart.update()
        }

    }

    return chart

}

export function setupYAxes(rootElement: HTMLElement, config: Chart.ChartConfiguration, fixedLeft: boolean, fixedRight: boolean) {

    if (!(fixedLeft || fixedRight)) return

    const left = fixedLeft && config.options.scales.yAxes.some(x => x.position == "left")
        ? createAxesContext(rootElement, "left")
        : undefined

    const right = fixedRight && config.options.scales.yAxes.some(x => x.position == "right")
        ? createAxesContext(rootElement, "right")
        : undefined


    config.options.animation = {

        onComplete: c => {
            left && left.onComplete(c)
            right && right.onComplete(c)
        },

        onProgress: c => {
            left && left.onProgress(c)
            right && right.onProgress(c)
        }

    }


}

export function createAxesContext(rootElement: HTMLElement, position: "left" | "right") {

    const scale = window.devicePixelRatio || 1
    const s = (d: number) => Math.ceil(d * scale)

    let rectangleSet = false

    // From https://jsfiddle.net/EmmaLouise/eb1aqpx8/3/

    return {

        onComplete: ({ chart }: { chart: ExtendedChart }) => {
            if (!rectangleSet) {

                var sourceCanvas = chart.chart.canvas;

                const { copyWidth, copyHeight, copyLeft } = getCopyDimensions(chart, position)

                var targetCtx = (rootElement.querySelector(`.axes.${position} canvas`) as HTMLCanvasElement).getContext("2d")

                targetCtx.scale(scale, scale)
                targetCtx.canvas.width = s(copyWidth)
                targetCtx.canvas.height = s(copyHeight)

                targetCtx.canvas.style.width = `${copyWidth}px`;
                targetCtx.canvas.style.height = `${copyHeight}px`;

                targetCtx.canvas.parentElement.style.width = `${copyWidth}px`;

                targetCtx.drawImage(
                    sourceCanvas,
                    s(copyLeft), 0, s(copyWidth), s(copyHeight),
                    0, 0, s(copyWidth), s(copyHeight)
                )

                var sourceCtx = sourceCanvas.getContext('2d');

                // Normalize coordinate system to use css pixels.

                sourceCtx.clearRect(s(copyLeft), 0, s(copyWidth), s(copyHeight))
                rectangleSet = true;
            }
        },

        onProgress: function({ chart }: { chart: ExtendedChart }) {
            if (rectangleSet === true) {

                const { copyWidth, copyHeight, copyLeft } = getCopyDimensions(chart, position)
                const s = (d: number) => Math.ceil(d * window.devicePixelRatio)

                const sourceCtx = chart.chart.canvas.getContext('2d')
                sourceCtx.clearRect(s(copyLeft), 0, s(copyWidth), s(copyHeight))

            }
        }

    }
}

function getCopyDimensions(chart: Chart, position: "left" | "right") {

    const d = getAxesDimensions(chart, position)

    const r = {
        copyLeft: position == "left" ? 0 : Math.ceil(d.left),
        copyWidth: Math.ceil(d.width),
        copyHeight: Math.ceil(d.height + d.top + 10)
    }

    return r


}

function getAxesDimensions(chart: Chart, position: "left" | "right") {

    const scales = chart["scales"] as {
        [id: string]: {
            top: number,
            left: number,
            width: number,
            height: number,
            options: Chart.CommonAxe
        }
    }

    const a = Object.keys(scales).map(k => scales[k]).filter(s => s.options.position == position)

    const top = arrayMin(a, a => a.top)

    return {
        left: arrayMin(a, a => a.left),
        top,
        width: sum(a, a => a.width),
        height: arrayMax(a, a => a.top + a.height) - top
    }

}
